/**
 * useDocumentTitle Hook
 * Keeps document.title in sync with the active conversation and total unread count.
 * Examples: "(3) # general — HighFive", "(3) Иван Петров — HighFive", "HighFive"
 *
 * Mount once (e.g. in MainLayout). Resilient to missing counts/data.
 */

import { useEffect } from 'react';
import { useChannels } from '@/hooks/useChannels';
import { useDMThreads } from '@/hooks/useDMs';
import { useAppStore } from '@/store/app.store';
import { useAuthStore } from '@/store/auth.store';

const APP_NAME = 'HighFive';

export const useDocumentTitle = () => {
  const { activeChannelId, activeDMThreadId } = useAppStore();
  const { data: channels } = useChannels();
  const { data: threads } = useDMThreads();
  const { user: currentUser } = useAuthStore();

  useEffect(() => {
    // Total unread across channels + DM threads (resilient to undefined counts)
    const channelUnread = (channels ?? []).reduce(
      (sum, ch) => sum + (ch.unread_count ?? 0),
      0
    );
    const dmUnread = (threads ?? []).reduce(
      (sum, t) => sum + (t.unread_count ?? 0),
      0
    );
    const totalUnread = channelUnread + dmUnread;

    // Active conversation label
    let activeLabel: string | null = null;

    if (activeChannelId) {
      const channel = channels?.find((ch) => ch.id === activeChannelId);
      if (channel) {
        activeLabel = `# ${channel.name}`;
      }
    } else if (activeDMThreadId) {
      const thread = threads?.find((t) => t.id === activeDMThreadId);
      if (thread) {
        const otherMembers =
          thread.members?.filter((m) => m.id !== currentUser?.id) ?? [];
        if (thread.is_group) {
          const names = otherMembers
            .map((m) => m.display_name?.split(' ')[0] || m.email)
            .filter(Boolean);
          activeLabel = names.length > 0 ? names.join(', ') : 'Групов чат';
        } else {
          activeLabel =
            otherMembers[0]?.display_name || otherMembers[0]?.email || 'Разговор';
        }
      }
    }

    const prefix = totalUnread > 0 ? `(${totalUnread}) ` : '';
    document.title = activeLabel
      ? `${prefix}${activeLabel} — ${APP_NAME}`
      : `${prefix}${APP_NAME}`;
  }, [activeChannelId, activeDMThreadId, channels, threads, currentUser?.id]);
};
